
import { defineComponent, onBeforeMount, onMounted, reactive } from 'vue'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @ui-kit
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewTable from '@/ui-kit/NewTable/index.vue'

import ReportRow from '@/modules/NewDispatcher/components/ReportRow/index.vue'
import ReportInfoRow from '@/modules/NewDispatcher/components/ReportInfoRow/index.vue'

// @types
import { Courier, CourierReport, GetCouriersReportsParams } from '@/new-services/types/couriers'

// @new-services
import couriersAPI from '@/new-services/couriers.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useRoute } from 'vue-router'
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

const COLUMNS = [
    {
        key: 'id',
        name: 'ID',
    },
    {
        key: 'fullName',
        name: 'ФИО',
    },
    {
        key: 'delivery',
        name: 'Доставка',
    },
    {
        key: 'take',
        name: 'Заборы',
    },
    {
        key: 'hour',
        name: 'Час',
    },
    {
        key: 'waves',
        name: 'Волны',
    },
    {
        key: 'cash',
        name: 'Наличка',
    },
    {
        key: 'codPayment',
        name: 'Налож.',
    },
    {
        key: 'hasReturnDelivery',
        name: 'Возврат',
    },
    {
        key: 'isClosed',
        name: '',
    },
]

export const OPTIONS = [
    { id: 1, name: 'Нет' },
    { id: 2, name: 'Да' },
]

export default defineComponent({
    components: {
        NewModal,
        PageTitle,
        NewButton,
        NewInput,
        NewTable,
        ReportRow,
        ReportInfoRow,
        Loader,
        NewSelect,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const new_dispatcher_store = useNewDispatcherStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            reports: [] as CourierReport[],
            expanded_reports: [] as number[],
        })

        const close_day_modal = reactive({
            is_opened: false,
            undelivered_invoices: [] as string[],
            courier: null as string | null,
        })

        const data_state = reactive({
            couriers: {
                is_loading: false,
                is_failed: false,
                list: [] as Courier[],
            },
        })

        const filters = reactive({
            fromDate: null as string | null,
            toDate: null as string | null,
            hasReturn: null as number | null,
            hasCash: null as number | null,
            hasCodPayment: null as number | null,
        })

        const filters$ = useVuelidate(
            {
                fromDate: { required },
            },
            filters
        )

        const loadReports = async () => {
            filters$.value.$touch()
            if (filters$.value.$invalid) return

            try {
                state.is_loading = true

                const response = await couriersAPI.getCouriersReports({
                    ...filters,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                } as GetCouriersReportsParams)

                state.reports = response.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onFromDateChange = (date: string | null) => {
            if (!date) return
            loadReports()
        }

        const isReportExpanded = (report_id: number) => {
            return state.expanded_reports.includes(report_id)
        }

        const toggleReport = (report_id: number) => {
            if (isReportExpanded(report_id)) {
                const index = state.expanded_reports.findIndex((rprt_id) => rprt_id === report_id)
                if (index >= 0) {
                    state.expanded_reports.splice(index, 1)
                }
            } else {
                state.expanded_reports.push(report_id)
            }
        }

        const openCloseDayModal = (undelivered_invoices: string[], courier: string) => {
            close_day_modal.courier = courier
            close_day_modal.undelivered_invoices = undelivered_invoices
            close_day_modal.is_opened = true
        }

        const closeCloseDayModal = () => {
            close_day_modal.is_opened = false
            close_day_modal.undelivered_invoices = []
            close_day_modal.courier = null
        }

        const onDayClosed = ({
            index,
            parentIndex,
            undeliveredInvoices,
        }: {
            index: number
            parentIndex: number
            undeliveredInvoices: string[]
        }) => {
            if (!undeliveredInvoices.length) {
                // @ts-ignore
                state.reports[parentIndex].info[index].isClosed = true
            }

            if (undeliveredInvoices.length) {
                openCloseDayModal(undeliveredInvoices, state.reports[parentIndex].fullName)
            }
        }

        const resetFilters = () => {
            filters.fromDate = null
            filters.toDate = null
            filters.hasReturn = null
            filters.hasCash = null
            filters.hasCodPayment = null

            state.reports = []

            loadReports()
        }

        const loadSavedDate = () => {
            const date = route.query.date as string | null

            if (!date) return

            filters.fromDate = date || null
            onFromDateChange(date)
        }

        const setTodayDate = () => {
            let today = new Date().toISOString().slice(0, 10)

            filters.fromDate = today
            filters.toDate = today
        }

        onBeforeMount(() => {
            setTodayDate()
            loadSavedDate()
        })

        return {
            COLUMNS,
            state,
            close_day_modal,
            data_state,
            filters,
            filters$,
            onFromDateChange,
            isReportExpanded,
            toggleReport,
            onDayClosed,
            closeCloseDayModal,
            resetFilters,
            //
            OPTIONS,
        }
    },
})
